import React from 'react'
import {
  Box,
  Checkbox,
  Flex,
  InputGroup,
  Text,
  Link,
  Button,
} from '@clear/design-system'
import { Form, StepSubTitle } from '@components/layout/Form'
import PageContent from '@components/layout/PageContent'
import VerificationCodeForm from '@components/VerificationCodeForm'
import { FormSteps } from '@pages/auth'
import AuthHeader from './AuthHeader'
import BannerAlert from '@components/shared/BannerAlert'
import useMedia from 'use-media'
import { LAYOUT_BREAKPOINT } from '@styles/theme'

const AuthAdvisoryBanner = () => {
  const isDesktopLayout = useMedia({ minWidth: LAYOUT_BREAKPOINT })

  return (
    <BannerAlert
      data-testid="auth-advisory-tooltip"
      maxWidth="42rem"
      marginTop={isDesktopLayout ? '3rem' : '2rem'}
      marginBottom="2rem"
      padding="0.75rem 0.75rem 0.75rem 1rem"
      className="bg-dhs-blue-10 border border-solid border-dhs-blue-30"
      borderRadius="0.5rem"
      iconProps={{
        name: 'shield',
        size: '25px',
        color: 'neutralsTextBlack',
        fontFamily: 'Inter-Bold',
      }}
    >
      <Text
        data-testid="biographic-information-tooltip-text"
        fontSize={isDesktopLayout ? '1rem' : '0.75rem'}
        color="neutralsTextBlack"
        lineHeight="1.15rem"
        fontFamily="Inter-Regular"
      >
        <strong>Why do we need this?</strong> Your email and phone are used to
        verify your identity and receive updates on your application status.
        This enrolls you in TSA&nbsp;PreCheck® only, not a CLEAR membership.
      </Text>
    </BannerAlert>
  )
}

const V2AuthFlow = ({
  step,
  verificationFormData,
  onVerificationSubmit,
  otpFailureMessage,
  setOtpFailureMessage,
  isLoading,
  handleSubmit,
  onSubmitContact,
  register,
  errors,
  isValid,
}) => {
  const isDesktopLayout = useMedia({ minWidth: LAYOUT_BREAKPOINT })

  return (
    <>
      <AuthHeader />
      <PageContent display="flex" flexDirection="column" alignItems="center">
        <Box
          width={['95%', '527px']}
          marginLeft="auto"
          marginRight="auto"
          border="1px solid #CCCED1"
          borderRadius="1rem"
          paddingX={['1rem', '1.5rem']}
          paddingY={['1.5rem', '1.5rem']}
          backgroundColor="neutralsWhite"
        >
          {step === FormSteps.VERIFICATION && (
            <VerificationCodeForm
              showLogo={false}
              formData={verificationFormData}
              onSubmit={onVerificationSubmit}
              otpFailureMessage={otpFailureMessage}
              setOtpFailureMessage={setOtpFailureMessage}
              isLoading={isLoading}
              onSubmitContact={onSubmitContact}
              verificationFormData={verificationFormData}
            />
          )}
          {step === FormSteps.CONTACT && (
            <>
              <StepSubTitle
                data-testid="auth-inset-title"
                textAlign="center"
                fontSize={!isDesktopLayout ? 4 : 5}
                fontWeight={700}
                lineHeight="1.75rem"
                mb="1.5rem"
              >
                Enter Your Email and Phone
              </StepSubTitle>
              <StepSubTitle
                textAlign="center"
                fontSize={2}
                fontWeight={400}
                lineHeight="normal"
                mb="1.5rem"
              >
                Enter your email and mobile number. You'll be sent a one-time
                code to sign in / create an account.
              </StepSubTitle>

              <Form onSubmit={handleSubmit(onSubmitContact)}>
                <InputGroup
                  data-cy="email"
                  placeholder={'Email'}
                  {...register('email')}
                  error={errors.email?.message}
                  mb={2}
                  containerProps={{ padding: 0 }}
                />

                <Flex flexDirection="row">
                  <InputGroup
                    data-cy={'countryCode'}
                    name="countryCode"
                    disabled
                    value="+1"
                    containerProps={{
                      padding: 0,
                      width: '20%',
                    }}
                  />

                  <InputGroup
                    data-cy="phoneNumber"
                    placeholder={'Cell Phone Number'}
                    {...register('phoneNumber')}
                    error={errors.phoneNumber?.message}
                    containerProps={{
                      padding: 0,
                      marginLeft: '10px',
                      width: '90%',
                    }}
                  />
                </Flex>
                <Checkbox
                  text={
                    <Text
                      as="p"
                      color="neutralsTextGray"
                      variant="body"
                      fontSize="12px"
                    >
                      By providing my telephone number and checking this box, I
                      agree to the{' '}
                      <Link
                        target="blank"
                        variant="regular"
                        color="brandDarkBlue"
                        href="/terms"
                      >
                        Terms & Conditions
                      </Link>{' '}
                      and I consent to receive multifactor text messages related
                      to my TSA&nbsp;PreCheck® account.
                    </Text>
                  }
                  data-cy="acceptTerms"
                  {...register('acceptTerms')}
                  containerProps={{
                    padding: 0,
                    marginY: '1.5rem',
                  }}
                />

                <Button
                  data-testid="send-verification-code-button"
                  data-cy="sendLoginCode"
                  type="submit"
                  text="Get Started"
                  disabled={
                    Boolean(Object.keys(errors).length) || !isValid || isLoading
                  }
                  height="3rem !important"
                />
              </Form>
            </>
          )}
        </Box>
        <AuthAdvisoryBanner />
      </PageContent>
    </>
  )
}

export default V2AuthFlow
